import React, { useState, useEffect, useContext, useRef } from 'react';
import { Language_Text_Editor } from './language_text_editor.js';
import { Answers_Builder } from './answers_builder.js';
import { Answer_Editor } from './answer_editor.js';
import { Condition_Editor } from './condition_editor.js';
import QuestionValidationEditor from './components/QuestionValidationEditor.js';
import { FaCheck, FaSave } from 'react-icons/fa';
import { Colors, generateHierarchicalCodes } from './form_builder.js';
import UserContext from '../../../components/User_Context.js';
import * as uploadManager from "../../../components/ui/fileupload.js";
import waiting from "../../../assets/images/waiting.svg";
import folderSearch from '../../../assets/icon/folder-search.svg';
import cloud_icon from "../../../assets/images/cloud.svg";
import IconSelector from '../../../components/Icon_Selector.js';
import IconFolder from '../../../assets/icon/mimetype/icons8-folder.svg';
import * as utils from "../../../components/utils.js";
import * as icons from "../../../components/fileTypesIcons.js";
import { FileFolderSelector } from '../../../components/fileFolderSelector.js';
import * as api from "../../../api/apiConnector.js";
import { useTranslation } from 'react-i18next';
import * as menu from "../../../assets/menus/menu.js";
import Choices from '../../../components/Choices.js';
import EditorJSTextEditor from './components/EditorJSTextEditor.js';
import EditorJSRenderer from './components/EditorJSRenderer.js';
import TableColumnsBuilder from './components/TableColumnsBuilder.js';

// Aggiungiamo stili CSS per supportare le aree di trascinamento
const styles = {
    draggableArea: {
        border: '2px dashed #ccc',
        borderRadius: '5px',
        minHeight: '150px',
        transition: 'all 0.3s ease',
        backgroundColor: '#f8f8f8'
    },
    draggableAreaActive: {
        border: '2px dashed #007bff',
        backgroundColor: 'rgba(0, 123, 255, 0.1)'
    }
};

/**
 * Question_Editor
 *
 * @param {object} question - the question object
 * @param {string} language - current language
 * @param {function} onConfirm - callback(updatedQuestion)
 */
const Question_Editor = ({ 
    question, 
    language, 
    onConfirm, 
    formSections = [],
    currentLocalSection = null,
    colors = Colors,
    isEditing = false,
    parentEditContext = null
}) => {
    const { currentUser } = useContext(UserContext);
    const { t } = useTranslation();
    const [localQuestion, setLocalQuestion] = useState({
        questionCode: '',
        questionType: 'single',
        mandatory: false,
        invisible: false,
        allowFileUpload: false,
        allowLinkUpload: false,
        allowTextUpload: false,
        allowObservations: false,
        hidePartially: false,
        questionText: [],
        description: [],
        answers: [],
        visibilityCondition: null,
        files: [],
        selectedVocabulary: null,
        selectedCustomVocabulary: null,
        defaultAnswers: [], // Add default answers array
        // Table specific fields
        tableColumns: [],
        tableSettings: {
            minRows: 1,
            maxRows: 10,
            allowAddRows: true,
            allowDeleteRows: true,
            showRowNumbers: true
        },
        // Period specific fields
        periodIncludeTime: false
    });

    // Reference for Choices component
    const vocabularyRef = useRef(null);
    const customVocabularyRef = useRef(null);

    // State for custom vocabularies
    const [customVocabularies, setCustomVocabularies] = useState([]);
    const [loadingCustomVocabularies, setLoadingCustomVocabularies] = useState(false);

    /**
     * Load custom vocabularies from API
     */
    const loadCustomVocabularies = async () => {
        if (loadingCustomVocabularies) return;
        
        setLoadingCustomVocabularies(true);
        try {
            const result = await api.sendMessage("admin/form_custom_vocs/get_all", {
                user: currentUser(),
                body: {
                    paginate: false // Get all vocabularies without pagination
                }
            });

            if (result.success && result.vocabularies) {
                setCustomVocabularies(result.vocabularies);
            } else {
                console.error("Error loading custom vocabularies:", result);
                setCustomVocabularies([]);
            }
        } catch (error) {
            console.error("Error calling custom vocabularies API:", error);
            setCustomVocabularies([]);
        } finally {
            setLoadingCustomVocabularies(false);
        }
    };

    /**
     * Text Localization Helper Function
     * 
     * Retrieves text in the current language from a multilingual array
     * Falls back to any available language or default text if current language not found
     * 
     * @param {Array} textArray - Array of text objects with language and text properties
     * @param {string} defaultText - Fallback text if no localized version found
     * @returns {string} The localized text or fallback
     */
    const getLocalizedText = (textArray, defaultText) => {
        // Return default if no array or empty array
        if (!textArray || !Array.isArray(textArray) || textArray.length === 0) return defaultText;
        
        // Look for text in current language
        const localizedItem = textArray.find(t => t && t.language === language);
        
        // If not found or empty, try any available language as fallback
        if (!localizedItem || !localizedItem.text || localizedItem.text.trim() === '') {
            const anyLanguageText = textArray.find(t => t && t.text && t.text.trim() !== '');
            return anyLanguageText?.text || defaultText;
        }
        
        return localizedItem.text;
    };

    // Helper function to extract plain text from EditorJS content
    const extractTextFromEditorJS = (content) => {
        if (!content) return '';
        
        try {
            // Try to parse as JSON (EditorJS format)
            const parsed = JSON.parse(content);
            if (parsed.blocks && Array.isArray(parsed.blocks)) {
                return parsed.blocks
                    .map(block => {
                        if (block.data && block.data.text) {
                            // Remove HTML tags from text
                            return block.data.text.replace(/<[^>]*>/g, '');
                        }
                        if (block.data && block.data.items) {
                            // Handle list items
                            return block.data.items.map(item => 
                                item.replace(/<[^>]*>/g, '')
                            ).join(', ');
                        }
                        return '';
                    })
                    .filter(text => text.trim())
                    .join(' ')
                    .trim();
            }
        } catch (e) {
            // If not JSON, treat as plain text and remove HTML tags
            return content.replace(/<[^>]*>/g, '').trim();
        }
        
        return content.toString().trim();
    };

    // Funzione per caricare le domande disponibili quando necessario
    const loadAvailableQuestions = React.useCallback(async () => {
        // Domande filtrate che appariranno prima della domanda corrente
        let filteredQuestions = [];
        let currentQuestionFound = false;
        // Set per tenere traccia degli ID delle domande già aggiunte per evitare duplicati
        const addedQuestionIds = new Set();
        
        // Crea una lista combinata di sezioni: prima le sezioni globali, poi la sezione locale corrente
        const allSections = [...(formSections || [])];
        
        // Se abbiamo una sezione locale, sostituisci quella corrispondente o aggiungila
        if (currentLocalSection) {
            const localSectionIndex = allSections.findIndex(s => s.id === currentLocalSection.id);
            if (localSectionIndex >= 0) {
                // Sostituisci la sezione esistente con quella locale aggiornata
                allSections[localSectionIndex] = currentLocalSection;
            } else {
                // Aggiungi la nuova sezione locale
                allSections.push(currentLocalSection);
            }
        }
        
        // Attraversa tutte le sezioni e le relative domande in ordine
        for (const section of allSections) {
            for (const q of section.questions || []) {
                // Se troviamo la domanda corrente, interrompiamo
                // Confrontiamo sia per ID che per questionCode per gestire domande esistenti senza ID
                if ((q.id && question.id && q.id === question.id) || 
                    (q.questionCode && question.questionCode && q.questionCode === question.questionCode)) {
                    currentQuestionFound = true;
                    break;
                }
                
                // Aggiungi solo domande con risposte (single, multiple, boolean, yesNoNA o pdca)
                if (q.questionType === 'single' || q.questionType === 'multiple' || q.questionType === 'boolean' || q.questionType === 'yesNoNA' || q.questionType === 'pdca') {
                    // Usa l'ID della domanda per evitare duplicati
                    const questionId = q.id || q.questionCode;
                    
                    // Se questa domanda è già stata aggiunta, saltala
                    if (addedQuestionIds.has(questionId)) {
                        continue;
                    }
                    
                    // Extract readable text from EditorJS content
                    const questionTextContent = q.questionText?.find(t => t.language === language)?.text || '';
                    const readableText = extractTextFromEditorJS(questionTextContent) || q.questionCode || `Question ${q.id}`;
                    
                    filteredQuestions.push({
                        id: questionId,
                        answers: q.answers?.map(a => ({
                            id: a.id || a.answerCode, // Fallback al answerCode se non c'è ID
                            code: a.value || a.answerCode,
                            text: a.answerText?.find(t => t.language === language)?.text || a.value || a.answerCode
                        })) || [],
                        code: q.questionCode,
                        text: readableText
                    });
                    
                    // Aggiungi l'ID al set per evitare duplicati futuri
                    addedQuestionIds.add(questionId);
                }
            }
            
            // Se abbiamo trovato la domanda corrente, interrompiamo anche il ciclo esterno
            if (currentQuestionFound) {
                break;
            }
        }
        
        return filteredQuestions;
    }, [formSections, currentLocalSection, question.id, question.questionCode, language]);

    const [editingAnswerIndex, setEditingAnswerIndex] = useState(null);
    const [showDefaultAnswers, setShowDefaultAnswers] = useState(false);
    let system_message_timer = null;

    // Labels are now managed centrally in menu.js via form_builder_labels.js
    // No need to set up individual labels here as they are loaded automatically

    // Assicuriamoci che lo stile CSS necessario sia disponibile
    useEffect(() => {
        if (!document.getElementById('dnd-styles')) {
            const styleSheet = document.createElement('style');
            styleSheet.id = 'dnd-styles';
            styleSheet.innerHTML = `
                .draggableArea {
                    border: 2px dashed #007bff !important;
                    background-color: rgba(0, 123, 255, 0.1) !important;
                    transition: all 0.3s ease;
                    box-shadow: 0 0 10px rgba(0, 123, 255, 0.2) !important;
                    transform: scale(1.01);
                }
                
                #draggableArea {
                    border: 2px dashed #ccc;
                    border-radius: 8px;
                    min-height: 150px;
                    transition: all 0.3s ease;
                    background-color: #f8f9fa;
                }
                
                #draggableArea:hover {
                    border-color: #adb5bd;
                    background-color: #f1f3f5;
                }
            `;
            document.head.appendChild(styleSheet);
        }
        
        return () => {
            const styleSheet = document.getElementById('dnd-styles');
            if (styleSheet) {
                document.head.removeChild(styleSheet);
            }
        };
    }, []);

    useEffect(() => {
        setLocalQuestion({
            id: question.id, // Preserve the question ID
            questionCode: question.questionCode || '',
            questionType: question.questionType || 'single',
            mandatory: question.mandatory ?? false,
            invisible: question.invisible ?? false,
            allowFileUpload: question.allowFileUpload ?? false,
            allowLinkUpload: question.allowLinkUpload ?? false,
            allowTextUpload: question.allowTextUpload ?? false,
            allowObservations: question.allowObservations ?? false,
            hidePartially: question.hidePartially ?? false,
            questionText: question.questionText || [],
            description: question.description || [],
            answers: question.answers || [],
            visibilityCondition: question.visibilityCondition,
            files: question.files || [],
            selectedVocabulary: question.selectedVocabulary || null,
            selectedCustomVocabulary: question.selectedCustomVocabulary || null,
            defaultAnswers: question.defaultAnswers || [], // Initialize defaultAnswers
            // Table specific fields
            tableColumns: question.tableColumns || [],
            tableSettings: question.tableSettings || {
                minRows: 1,
                maxRows: 10,
                allowAddRows: true,
                allowDeleteRows: true,
                showRowNumbers: true
            },
            // Period specific fields
            periodIncludeTime: question.periodIncludeTime ?? false,
            // Validation fields
            validation: question.validation || []
        });
        
        // Auto-expand default answers section if there are already default answers set
        if (question.defaultAnswers && question.defaultAnswers.length > 0) {
            setShowDefaultAnswers(true);
        } else {
            // Close the section if no default answers
            setShowDefaultAnswers(false);
        }
    }, [question]);

    // Effect to handle hiding/showing "parzialmente" answers for PDCA questions
    useEffect(() => {
        if (localQuestion.questionType === 'pdca' && localQuestion.answers.length > 0) {
            const updatedAnswers = localQuestion.answers.map(answer => {
                // Check if answer text contains "parzialmente" (case insensitive)
                const answerText = answer.answerText?.find(t => t.language === language)?.text || '';
                const containsPartially = answerText.toLowerCase().includes('parzialmente');
                
                if (containsPartially) {
                    // Set invisible based on hidePartially flag
                    return {
                        ...answer,
                        invisible: localQuestion.hidePartially
                    };
                }
                
                return answer;
            });
            
            // Only update if there are actual changes
            const hasChanges = updatedAnswers.some((answer, index) => 
                answer.invisible !== localQuestion.answers[index].invisible
            );
            
            if (hasChanges) {
                setLocalQuestion(prev => ({ ...prev, answers: updatedAnswers }));
            }
        }
    }, [localQuestion.hidePartially, localQuestion.questionType, language]);

    useEffect(() => {
        // Forza la tabella dei file a aggiornarsi
        if (window.$.fn && window.$.fn.dataTable) {
            setTimeout(() => {
                try {
                    if (window.$.fn.dataTable.isDataTable('.file-req-table')) {
                        window.$('.file-req-table').DataTable().destroy();
                    }
                    window.$('.file-req-table').DataTable({
                        paging: false,
                        searching: false,
                        info: false
                    });
                } catch (err) {
                    console.warn("DataTable initialization error:", err);
                }
            }, 300);
        }
    }, [localQuestion.files]);

    const handleConfirm = () => {
        // For open questions, ensure we generate an ID for the answer
        if (localQuestion.questionType === 'open' || localQuestion.questionType === 'openNumber') {
            // Use async function with awaits inside an IIFE
            (async () => {
                try {
                    // Get a new GUID for the open answer
                    const guid = await getGuid();
                    if (guid) {
                        // Create an answer object with the generated ID
                        const updatedQuestion = {
                            ...localQuestion,
                            openAnswerId: guid
                        };
                        onConfirm(updatedQuestion);
                    } else {
                        console.error("Failed to generate GUID for open answer");
                        onConfirm(localQuestion);
                    }
                } catch (error) {
                    console.error("Error generating GUID for open answer:", error);
                    onConfirm(localQuestion);
                }
            })();
        } else {
            onConfirm(localQuestion);
        }
    };

    const updateAnswer = (index, updatedAnswer) => {
        const updatedAnswers = [...localQuestion.answers];
        updatedAnswers[index] = {
            ...updatedAnswers[index],
            ...updatedAnswer
        };
        setLocalQuestion(prev => ({ ...prev, answers: updatedAnswers }));
    };

    // File handling functions
    const normItem = (item) => {
        if (item.ItemLastUpdated) item.ItemLastUpdated = new Date(item.ItemLastUpdated);
        if (item.ItemCreated) item.ItemCreated = new Date(item.ItemCreated);
        if (item.ItemDeleted) item.ItemDeleted = new Date(item.ItemDeleted);
    };

    const selectFileModal = async () => {
        let _modal = window.$(`<div id="select-file-modal" class="modal modal-xl" tabindex="-1">
          <div class="modal-dialog modal-dialog-scrollable">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title">${t('chooseFiles', { ns: 'labels' })}</h5>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div class="modal-body">
                <p>Modal body text goes here.</p>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">${t('close', { ns: 'labels' })}</button>
                <button type="button" class="btn btn-primary" id="upload-device-file-btn">
                  <i class="fa fa-upload me-1"></i> ${t('uploadFromDevice', { ns: 'labels' })}
                </button>
                <input type="file" id="device-file-input" style="display: none;" />
              </div>
            </div>
          </div>
        </div>`);

        window.$("#select-file").remove();
        window.$("body").append(_modal);
        _modal.modal("show");
        const modal_body = _modal.find(".modal-body");

        // Create a hidden file input
        const fileInput = window.$("#device-file-input");
        
        // Handle "Upload file from device" button click
        window.$("#upload-device-file-btn").on("click", () => {
            // Trigger the hidden file input
            fileInput.trigger('click');
        });
        
        // Handle file selection from device
        fileInput.on('change', async (e) => {
            if (e.target.files && e.target.files.length > 0) {
                // Close the modal
                window.$("#select-file-modal").modal("hide");
                
                show_system_message({
                    timeOut: 0,
                    message: `<strong>${t('uploading', { ns: 'labels' })}</strong> ${e.target.files[0].name}...`
                });
                
                // Upload the file using the same handler as drag & drop
                upload({
                    files: Array.from(e.target.files),
                    parentId: 'formQuestionsFiles',
                    onFileAdded: (files) => {
                        if (Array.isArray(files) && files.length > 0) {
                            show_system_message({
                                timeOut: 1500,
                                message: `<strong>${t('added', { ns: 'labels' })}</strong> ${files[0].Name}`
                            });
                        }
                    }
                });
            }
        });

        // Custom function to show only files from a specific folder
        const showFilesOnly = async () => {
            try {
                // Clear the container first
                modal_body.html(`<div class="text-center my-3"><img src="${waiting}" style="width: 50px;"/> ${t('loadingFiles', { ns: 'labels' })}</div>`);
                
                // Get the folder ID for question attachments
                const folderId = 'formQuestionsFiles'; 
                
                // Get all files from the specified folder
                const result = await api.sendMessage("dms/getFolder", {
                    user: currentUser(),
                    body: {
                        id: folderId,
                        addPath: true
                    }
                });
                
                if (result?.content?.files && Array.isArray(result.content.files)) {
                    // Clear loading message
                    modal_body.empty();
                    
                    // Create a list to display files
                    const fileList = window.$('<div class="list-group"></div>');
                    modal_body.append(fileList);
                    
                    if (result.content.files.length === 0) {
                        fileList.append(`<div class="list-group-item">${t('noFilesFound', { ns: 'labels' })}</div>`);
                    } else {
                        // Add each file to the list
                        result.content.files.forEach(file => {
                            // Normalize dates
                            if (file.ItemLastUpdated) file.ItemLastUpdated = new Date(file.ItemLastUpdated);
                            if (file.ItemCreated) file.ItemCreated = new Date(file.ItemCreated);
                            if (file.ItemDeleted) file.ItemDeleted = new Date(file.ItemDeleted);
                            
                            // Get appropriate icon for file type
                            const fileIcon = icons.iconMapping(file.MimeType);
                            
                            // Create list item
                            const listItem = window.$(`
                                <div class="list-group-item list-group-item-action d-flex align-items-center" style="cursor: pointer;">
                                    <div class="me-3">
                                        <img src="${fileIcon}" alt="File icon" style="width: 24px; height: 24px;">
                                    </div>
                                    <div class="flex-grow-1">
                                        <h6 class="mb-1">${file.Name}</h6>
                                        <small class="text-muted">
                                            ${t('lastUpdated', { ns: 'labels' })}: ${(file.ItemLastUpdated || file.ItemCreated).toLocaleDateString()}
                                        </small>
                                    </div>
                                </div>
                            `);
                            
                            // Add click event
                            listItem.on('click', async () => {
                                show_system_message({
                                    timeOut: 0,
                                    message: `<strong>${t('adding', { ns: 'labels' })} ${file.Name}</strong>...`
                                });
                                
                                const result = await addItem(file, "file");
                                
                                if (result.addedd) {
                                    show_system_message({
                                        timeOut: 1500,
                                        message: `<strong>${t('added', { ns: 'labels' })} ${file.Name}</strong>...`
                                    });
                                    
                                    // Close the modal after adding
                                    window.$("#select-file-modal").modal("hide");
                                }
                            });
                            
                            fileList.append(listItem);
                        });
                    }
                } else {
                    modal_body.html(`<div class="alert alert-warning">${t('failedToRetrieveFiles', { ns: 'labels' })}</div>`);
                }
            } catch (error) {
                console.error("Error loading files:", error);
                modal_body.html(`<div class="alert alert-danger">${error.message || t('failedToRetrieveFiles', { ns: 'labels' })}</div>`);
            }
        };
        
        // Call function to show files only
        showFilesOnly();
    };

    // todo
    const _getAllFileInFolder = async (parentId) => {
        var result = await api.sendMessage("dms/retrieveAllFile", {
            user: currentUser(),
            body: {
                parentId: parentId
            }
        });

        return result.files;
    };

    const addItem = async (f, type) => {
        if (type == "folder") {
            let filesToAdd = [];
            var allFile = await _getAllFileInFolder(f.Id);

            if (allFile.length > 0) {
                const updatedFiles = [...localQuestion.files];
                // Aggiungi solo file non duplicati
                for (const file of allFile) {
                    if (!file.Id) file.Id = "file_" + new Date().getTime() + "_" + Math.random().toString(36).substr(2, 9);
                    normItem(file);
                    if (!updatedFiles.some(existingFile => existingFile.Id === file.Id)) {
                        updatedFiles.push(file);
                    }
                }
                
                setLocalQuestion(prev => ({ ...prev, files: updatedFiles }));
                
                return { addedd: allFile };
            } else { 
                return null;
            }
        } else if (type == "file") {
            // Assicurati che il file abbia un ID
            if (!f.Id) f.Id = "file_" + new Date().getTime() + "_" + Math.random().toString(36).substr(2, 9);
            
            // Normalizza le date
            normItem(f);
            
            // Verifica duplicati prima di aggiungere
            const updatedFiles = [...localQuestion.files];
            let isDuplicate = false;
            
            if (updatedFiles.some(existingFile => existingFile.Id === f.Id)) {
                isDuplicate = true;
                show_system_message({
                    timeOut: 1500,
                    message: `<strong>File ${f.Name} already exists</strong>`
                });
            } else {
                updatedFiles.push(f);
                setLocalQuestion(prev => ({ ...prev, files: updatedFiles }));
            }
            
            return { addedd: isDuplicate ? null : f };
        } else { 
            return null;
        }
    };

    const show_system_message = (params) => {
        const { message, timeOut = 2000 } = params;

        const inner_div = window.$(`<div class="col-xl-12">
            <div class="card dz-card">
                <div class="card-header flex-wrap border-0">
                    <div>
                        <h4 class="card-title">AI Plan</h4>
                    </div>											
                </div>
                <div class="tab-content">
                    <div class="tab-pane fade active show" role="tabpanel">
                        <div class="card-body pt-0">
                            <h6><span></span>
                                <span class="pull-end"></span>
                            </h6>                                                                        
                        </div>
                    </div>
                </div>
            </div>
        </div>`);

        let div = window.$("#chat-system-message");

        if (div.length == 0) {
            div = window.$(`<div id="chat-system-message" style="position: fixed; right: 20px; bottom: 30px; width: 350px; height: 100px; opacity: 0.75"></div>`);
            window.$("body").append(div);
            div.append(inner_div);
        }

        const message_div = div.find(".pull-end");

        message_div.html("");
        message_div.append(window.$("<div/>").html(message));

        if (system_message_timer) {
            clearTimeout(system_message_timer);
        }

        if (timeOut > 0) {
            system_message_timer = setTimeout(() => {
                div.remove();
            }, timeOut);
        }
    };
    
    const upload = async (params) => {
        params.user = currentUser();
        uploadManager.fileUpload({
            ...params,
            parentId: 'formQuestionsFiles', 
            onFileAdded: (files) => {
                if (Array.isArray(files) && files.length > 0) {
                    const updatedFiles = [...localQuestion.files];
                    
                    for (var f of files) {
                        // Normalizza le date e gli altri dati
                        normItem(f);
                        
                        // Controlla se il file esiste già
                        if (updatedFiles.find(x => x.Id === f.Id)) {
                            // Se esiste già, sostituiscilo
                            let index = updatedFiles.findIndex(x => x.Id === f.Id);
                            updatedFiles.splice(index, 1, f);
                        } else {
                            // Altrimenti aggiungilo
                            updatedFiles.push(f);
                        }
                    }
                    
                    // Imposta i file aggiornati nello stato
                    setLocalQuestion(prev => {
                        return { ...prev, files: updatedFiles };
                    });
                    
                    // Notifica all'utente
                    show_system_message({
                        timeOut: 1500,
                        message: `<strong>${files.length} file${files.length > 1 ? 's' : ''} added successfully</strong>`
                    });
                    
                    // Forza l'aggiornamento della DataTable se esiste
                    if (window.$.fn && window.$.fn.dataTable) {
                        setTimeout(() => {
                            try {
                                if (window.$.fn.dataTable.isDataTable('.file-req-table')) {
                                    window.$('.file-req-table').DataTable().destroy();
                                }
                                window.$('.file-req-table').DataTable({
                                    paging: false,
                                    searching: false,
                                    info: false
                                });
                            } catch (err) {
                                console.warn("DataTable initialization error:", err);
                            }
                        }, 100);
                    }
                }
                
                if (params.onFileAdded) params.onFileAdded(files);
            },
            onError: (error) => {
                console.error("Upload error:", error);
                show_system_message({
                    timeOut: 3000,
                    message: `<strong class="text-danger">Error uploading files: ${error}</strong>`
                });
            }
        });
    };

    const _frDraggingEffect = (e, show) => {
        const _path = localQuestion.questionCode || 'Question Files';

        const _panel = window.$(`            
            <div id="dragging-bubble" class="bg-primary"             
            style="box-shadow: 2px 2px 2px 1px rgb(0 0 0 / 20%); z-index: 10000; position: fixed; bottom: 20px; transform: translateX(-50%); left: 50%; width: 300px; height: 80px; border-radius: 25px; opacity: .1;">
            <div id="dragging-bubble-icon" style="position: absolute; margin-top: -75px; width: 100%; text-align: center;"><img style="width: 100px" src="${cloud_icon}" /></div>
            <div style="position: relative; left: 0; top: 20px; width: 100%; text-align: center; color: white;">Drop files to upload them to</div>
            <div style="padding: 25px; position: relative; left: 0; top: 10px; width: 100%; text-align: center; color: white; overflow: hidden; white-space: nowrap; text-overflow: ellipsis;"><i class="fa fa-folder-tree me-2"></i> ${_path}</div>
        </div>`);

        let _stopSignal = false;
        if (show) {
            if (window.$("#dragging-bubble").length == 0) {
                window.$("body").append(_panel);
                _panel.fadeTo("slow", 1);

                let _direction = "up";

                const _animate = () => {
                    const _icon = window.$("#dragging-bubble-icon");
                    if (_direction == "up") {
                        _icon.animate({ "top": "-=25px" }, "slow", () => {
                            _direction = "down";
                            if (!_stopSignal) _animate();
                        });
                    } else {
                        _icon.animate({ "top": "+=25px" }, "slow", () => {
                            _direction = "up";
                            if (!_stopSignal) _animate();
                        });
                    }
                }

                _animate();
            }
        } else {
            _stopSignal = true;

            const _icon = window.$("#dragging-bubble-icon");
            _icon.animate({ "top": "-=1125px" }, "medium", () => {
                window.$("#dragging-bubble").remove();
            });
        }
    };

    const getName = (item) => {
        if (!item || !item.item) {
            console.error("Invalid item passed to getName:", item);
            return <h6 className="text-danger">Invalid file data</h6>;
        }

        const getPath = () => {
            if (item.item?.Path?.Path) {
                if (item.item?.Path?.Path.length > 0) {
                    return (<><small>in </small>
                        <a href={`/dms/${item.item.Path.Path[item.item.Path.Path.length - 1].Id}`}>
                            {(item.item.Path.Path.map(x => {
                                if (item.item.Path.Path.findIndex(y => y == x) == 0) {
                                    return (<small>{x.Name}</small>);
                                } else {
                                    return (<><small> / </small><small>{x.Name}</small></>);
                                }
                            }))}</a>
                    </>);
                }
            }
            return null;
        }

        if (item.type === "folder") {
            return (<h6 style={{
                opacity: item.item.System_Item ? .5 : 1
            }}>
                <img width="28" height="28" src={IconFolder} alt='folder' />
                {item.item.Name || "Unknown folder"}</h6>);
        } else {
            return (<>
                <h6>
                    {item.item.MimeType ? 
                        <IconSelector icon={item.item.MimeType} /> : 
                        <i className="fa fa-file me-2"></i>}
                    {item.item.Name || "Unknown file"}
                </h6>
                {getPath()}
            </>);
        }
    };

    const getTableRow = (item) => {
        if (!item || !item.item || !item.item.Id) {
            console.error("Invalid item passed to getTableRow:", item);
            return null;
        }

        return (
            <tr key={`file_item.${item.item.Id}`} data-id={item.item.Id}>
                <td>
                    <div data-id={item.item.Id} className="file-name">
                        <div>
                            {getName(item)}
                        </div>
                    </div>
                </td>
                <td style={{ cursor: "pointer", textAlign: "center" }}
                    onClick={(e) => {
                        show_system_message({
                            timeOut: 0,
                            message: `
                                <strong>Deleting ${item.item.Name || "file"} from the list</strong>...
                                `});

                        const updatedFiles = [...localQuestion.files];
                        const indexToRemove = updatedFiles.findIndex(x => x.Id === item.item.Id);
                        
                        if (indexToRemove !== -1) {
                            updatedFiles.splice(indexToRemove, 1);
                            setLocalQuestion(prev => ({ ...prev, files: updatedFiles }));
                            
                            show_system_message({
                                timeOut: 1500,
                                message: `
                                    <strong>${item.item.Name || "File"} deleted correctly</strong>...
                                    `});
                        }
                    }}
                ><i className="fa fa-trash me-2 text-secondary"></i></td>
            </tr>
        );
    };

    const getItems = () => {
        if (!localQuestion.files || !Array.isArray(localQuestion.files) || localQuestion.files.length === 0) {
            return [];
        }
        
        let _files = localQuestion.files.map((i) => {
            // Assicurati che ogni file abbia un ID
            if (!i.Id) {
                i.Id = "file_" + new Date().getTime() + "_" + Math.random().toString(36).substr(2, 9);
            }
            
            // Clona l'oggetto per evitare mutazioni non intenzionali
            const itemCopy = { ...i };
            normItem(itemCopy);
            
            return {
                type: "file",
                item: itemCopy
            };
        });
        
        return _files;
    };
    
    const getTableRows = () => {
        let _items = getItems();
        if (_items.length === 0) {
            return (
                <tr>
                    <td colSpan="2" className="text-center">
                        {t('noFilesRequired', { ns: 'labels' })}
                    </td>
                </tr>
            );
        }
        
        return _items.map((i) => {
            const row = getTableRow(i);
            if (!row) {
                console.error("Failed to generate row for item:", i);
                return null;
            }
            return row;
        }).filter(row => row !== null);
    };

    const _drawFilesSection = () => {
        // Contatore per forzare il rendering in caso di aggiornamenti
        const fileCount = localQuestion.files?.length || 0;
        const fileItems = getItems();
        const hasFiles = fileItems.length > 0;
        
        return (
            <div className="col-12 px-0">
                <div className="card mb-3 w-100" style={{ backgroundColor: editContextColors.body }}>
                    <div className="card-header" 
                        style={{ backgroundColor: colors.secondaryLight }}>
                        <h6 className="mb-0">{t('files', { ns: 'labels' })} ({fileCount})</h6>
                    </div>
                    <div className="card-body">
                        <div className="col-lg-12 mb-3 row">
                            <h6 className="col-md-12">
                                {t('dragFilesHere', { ns: 'labels' })} {t('orClickToSelect', { ns: 'labels' })}
                                <img src={folderSearch} className='ms-2' title={t('chooseFiles', { ns: 'labels' })} 
                                    style={{ cursor: "pointer" }} onClick={selectFileModal} />
                            </h6>
                            
                            <div className="tab-content" style={{ width: "100%" }}>
                                <div className="tab-pane fade active show" id="document-list-panel" role="tabpanel" aria-labelledby="document-list-tab" tabIndex="0">
                                    <div className="container-fluid" 
                                        onMouseOver={(e) => {
                                            window.$("#draggableArea").removeClass("draggableArea");
                                            _frDraggingEffect();
                                        }}
                                        onDragOver={(e) => {
                                            window.$("#draggableArea").removeClass("draggableArea");
                                            _frDraggingEffect();
                                        }}
                                    >
                                        <div className='draggable-div' id="draggableArea"
                                            style={{ padding: '15px' }}
                                            onDragOver={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (e.dataTransfer && e.dataTransfer.items
                                                    && e.dataTransfer.items.length > 0
                                                ) {
                                                    for (const f of e.dataTransfer.items) {
                                                        if (f.kind == "file") {
                                                            const _icon = icons.iconMapping(f.type);
                                                        }
                                                    }
                                                }
                                            }}
                                            onMouseOut={(e) => {
                                                window.$("#draggableArea").removeClass("draggableArea");
                                            }}
                                            onDragEnter={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                window.$("#draggableArea").addClass("draggableArea");
                                                _frDraggingEffect(e, true);
                                            }}
                                            onDragEnd={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                window.$("#draggableArea").removeClass("draggableArea");
                                            }}
                                            onDragLeave={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }}
                                            onDrop={async (e) => {
                                                window.$("#draggableArea").removeClass("draggableArea");
                                                e.preventDefault();
                                                e.stopPropagation();
                                                
                                                try {
                                                    // Ottieni i file dal drag & drop
                                                    const _files = await uploadManager.getAllFileEntries(e.dataTransfer.items);
                                                    
                                                    if (_files.files && _files.files.length) {
                                                        // Mostra messaggio di caricamento
                                                        show_system_message({
                                                            timeOut: 0,
                                                            message: `<strong>Uploading ${_files.files.length} file(s)...</strong>`
                                                        });
                                                        
                                                        // Carica i file
                                                        upload({
                                                            files: _files.files, 
                                                            parentId: 'formQuestionsFiles'
                                                        });
                                                    }
                                                } catch (error) {
                                                    console.error("Error processing dropped files:", error);
                                                    show_system_message({
                                                        timeOut: 3000,
                                                        message: `<strong class="text-danger">Error processing files: ${error.message}</strong>`
                                                    });
                                                }
                                            }}
                                        >
                                            <div className="table-responsive">
                                                <table className="table file-req-table">
                                                    <thead>
                                                        <tr>
                                                            <th className="col-11">{t('name', { ns: 'labels' })}</th>
                                                            <th className="text-end col-1">{t('actions', { ns: 'labels' })}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {hasFiles ? (
                                                            fileItems.map((item, index) => (
                                                                <tr key={`file_row_${item.item.Id || index}`} data-id={item.item.Id}>
                                                                    <td>
                                                                        <div data-id={item.item.Id} className="file-name">
                                                                            <div>
                                                                                {getName(item)}
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                    <td style={{ cursor: "pointer", textAlign: "center" }}
                                                                        onClick={(e) => {
                                                                            show_system_message({
                                                                                timeOut: 0,
                                                                                message: `<strong>Deleting ${item.item.Name || "file"} from the list</strong>...`
                                                                            });
                                                                            
                                                                            // Rimuovi il file
                                                                            const updatedFiles = [...localQuestion.files];
                                                                            const indexToRemove = updatedFiles.findIndex(x => x.Id === item.item.Id);
                                                                            
                                                                            if (indexToRemove !== -1) {
                                                                                updatedFiles.splice(indexToRemove, 1);
                                                                                setLocalQuestion(prev => ({ ...prev, files: updatedFiles }));
                                                                                
                                                                                show_system_message({
                                                                                    timeOut: 1500,
                                                                                    message: `<strong>${item.item.Name || "File"} deleted correctly</strong>`
                                                                                });
                                                                            }
                                                                        }}
                                                                    ><i className="fa fa-trash me-2 text-secondary"></i></td>
                                                                </tr>
                                                            ))
                                                        ) : (
                                                            <tr>
                                                                <td colSpan="2" className="text-center">
                                                                    {t('noFilesRequired', { ns: 'labels' })}
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const getGuid = async () => {
        try {
            const result = await api.sendMessage("helper/guidGenerator", {
                user: currentUser(),
                body: {}
            });
            
            if (result.success && result.guid) {
                return result.guid;
            }
            console.error("Error getting GUID:", result);
            return null;
        } catch (err) {
            console.error("Error calling GUID generator:", err);
            return null;
        }
    };

    // Nuovo metodo per ottenere entità vuote
    const getEmptyEntity = async (entityType) => {
        try {
            const result = await api.sendMessage("darm/form_builder/getEmptyEntity", {
                user: currentUser(),
                body: {
                    type: entityType // 'section', 'question', 'answer'
                }
            });

            if (result.success && result.entity) {
                return result.entity;
            }
            console.error(`Error getting empty ${entityType}:`, result);
            
            // Fallback in caso di errore nell'API
            if (entityType === 'section') {
                return { name: [], description: [], questions: [], id: await getGuid() };
            } else if (entityType === 'question') {
                return { questionText: [], answers: [], id: await getGuid() };
            } else if (entityType === 'answer') {
                // Per le risposte otteniamo sempre un GUID dal server
                const guid = await getGuid();
                return { answerText: [], value: guid, id: guid };
            }
            
            return null;
        } catch (err) {
            console.error(`Error calling empty ${entityType} generator:`, err);
            return null;
        }
    };

    if (editingAnswerIndex !== null) {
        return (
            <Answer_Editor
                answer={localQuestion.answers[editingAnswerIndex]}
                language={language}
                onConfirm={(updatedAnswer) => {
                    updateAnswer(editingAnswerIndex, updatedAnswer);
                    setEditingAnswerIndex(null);
                }}
                colors={colors}
                isEditing={true}
            />
        );
    }

    // Specific edit context colors for questions
    const editContextColors = colors.editContextQuestion;

    // Add vocabulary options for the new question type
    const vocabularyOptions = [
        { id: 'functional_chart_roles', name: 'Functional Chart Roles' },
        { id: 'organizational_chart_roles', name: 'Organizational Chart Roles' },
        { id: 'business_unit_types', name: 'Business Unit Types' },
        { id: 'requisites_main_contexts', name: 'Requisites Main Contexts' },
        { id: 'hand_hygiene_oms_departments_category', name: 'Hand Hygiene OMS Departments Category' },
        { id: 'hand_hygiene_oms_operators', name: 'Hand Hygiene OMS Operators' },
        { id: 'risk_area', name: 'Risk Area' },
        { id: 'form_groups', name: 'Form Groups' }
    ];

    return (
        <div className="card shadow w-100" style={{ borderColor: editContextColors.border, borderWidth: '2px' }}>
            <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: editContextColors.header, color: colors.white }}>
                <h5 className="mb-0 d-flex align-items-center">
                    <span className="badge bg-white text-dark me-2">{t('editingQuestion', { ns: 'labels' })}</span>
                    <div style={{ color: colors.white }}>
                        <EditorJSRenderer
                            content={localQuestion.questionText.find(n => n.language === language)?.text || ''}
                            language={language}
                            defaultText={t('newQuestion', { ns: 'labels' })}
                            isHeader={true}
                        />
                    </div>
                </h5>
                <div>
                    <button className="btn btn-sm ms-2" 
                        style={{ backgroundColor: colors.white, color: colors.dark }}
                        onClick={handleConfirm}>
                        <FaCheck className="me-1" />
                        {t('done', { ns: 'labels' })}
                    </button>
                </div>
            </div>

            <div className="card-body">
                <div className="row g-3 w-100 mx-0">
                    <div className="col-12 px-0">
                        <div className="card w-100" style={{ backgroundColor: editContextColors.body }}>
                            <div className="card-header" 
                                style={{ backgroundColor: colors.secondaryLight }}>
                                <h6 className="mb-0">{t('questionDetails', { ns: 'labels' })}</h6>
                            </div>
                            <div className="card-body">
                                <div className="row mb-3">
                                    <div className="col-md-6 mb-3 mb-md-0">
                                        <label className="form-label fw-bold">{t('questionCode', { ns: 'labels' })}</label>
                                        <input type="text" 
                                            className="form-control" 
                                            value={localQuestion.questionCode}
                                            onChange={(e) => setLocalQuestion(prev => ({ ...prev, questionCode: e.target.value }))}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">{t('questionType', { ns: 'labels' })}</label>
                                        <select 
                                            className="form-select" 
                                            value={localQuestion.questionType}
                                            onChange={(e) => {
                                                const newType = e.target.value;
                                                let updatedQuestion = { ...localQuestion, questionType: newType };
                                                
                                                // Handle answers based on question type change
                                                if (newType === 'boolean') {
                                                    // Whether we're switching from yesNoNA or another type, we want to ensure
                                                    // we have proper boolean answers (true/false)
                                                    // Initialize answers array
                                                    updatedQuestion.answers = [];
                                                    
                                                    // Helper function to create and add an answer
                                                    const addBooleanAnswer = async (text, value, index) => {
                                                        const emptyAnswer = await getEmptyEntity('answer');
                                                        if (!emptyAnswer) {
                                                            console.error("Failed to get empty answer entity");
                                                            return;
                                                        }
                                                        
                                                        emptyAnswer.answerText = [{ language: language, text: text }];
                                                        emptyAnswer.value = value;
                                                        // Add automatic answer code
                                                        emptyAnswer.answerCode = generateHierarchicalCodes.generateAnswerCode(
                                                            localQuestion.questionCode,
                                                            index
                                                        );
                                                        updatedQuestion.answers.push(emptyAnswer);
                                                    };
                                                    
                                                    // Add boolean answers asynchronously
                                                    (async () => {
                                                        await addBooleanAnswer('Sì', 'true', 0);
                                                        await addBooleanAnswer('No', 'false', 1);
                                                        
                                                        // Clear default answers when switching to boolean
                                                        updatedQuestion.defaultAnswers = [];
                                                        
                                                        // Important: update the state after all answers are created
                                                        setLocalQuestion(updatedQuestion);
                                                    })();
                                                } else if (newType === 'yesNoNA') {
                                                    // Always set the three standard options when switching to yesNoNA
                                                    // Initialize answers array
                                                    updatedQuestion.answers = [];
                                                    
                                                    // Helper function to create and add an answer
                                                    const addYesNoNaAnswer = async (text, value, index) => {
                                                        const emptyAnswer = await getEmptyEntity('answer');
                                                        if (!emptyAnswer) {
                                                            console.error("Failed to get empty answer entity");
                                                            return;
                                                        }
                                                        
                                                        emptyAnswer.answerText = [{ language: language, text: text }];
                                                        emptyAnswer.value = value;
                                                        // Add automatic answer code
                                                        emptyAnswer.answerCode = generateHierarchicalCodes.generateAnswerCode(
                                                            localQuestion.questionCode,
                                                            index
                                                        );
                                                        updatedQuestion.answers.push(emptyAnswer);
                                                    };
                                                    
                                                    // Add Yes/No/NA answers asynchronously
                                                    (async () => {
                                                        await addYesNoNaAnswer('Sì', 'yes', 0);
                                                        await addYesNoNaAnswer('No', 'no', 1);
                                                        await addYesNoNaAnswer('Non Applicabile', 'na', 2);
                                                        
                                                        // Clear default answers when switching to yesNoNA
                                                        updatedQuestion.defaultAnswers = [];
                                                        
                                                        // Important: update the state after all answers are created
                                                        setLocalQuestion(updatedQuestion);
                                                    })();
                                                } else if (newType === 'pdca') {
                                                    // Set predefined PDCA answers
                                                    // Initialize array to store predefined answers
                                                    updatedQuestion.answers = [];
                                                    
                                                    // Helper function to create and add an answer
                                                    const addPdcaAnswer = async (text, value, index) => {
                                                        const emptyAnswer = await getEmptyEntity('answer');
                                                        if (!emptyAnswer) {
                                                            console.error("Failed to get empty answer entity");
                                                            return;
                                                        }
                                                        
                                                        emptyAnswer.answerText = [{ language: language, text: text }];
                                                        emptyAnswer.value = value;
                                                        // Add automatic answer code
                                                        emptyAnswer.answerCode = generateHierarchicalCodes.generateAnswerCode(
                                                            localQuestion.questionCode,
                                                            index
                                                        );
                                                        updatedQuestion.answers.push(emptyAnswer);
                                                    };
                                                    
                                                    // Add all PDCA answers asynchronously
                                                    (async () => {
                                                        await addPdcaAnswer('Pianificato', 'planned', 0);
                                                        await addPdcaAnswer('Parzialmente pianificato', 'partially_planned', 1);
                                                        await addPdcaAnswer('Attuato', 'implemented', 2);
                                                        await addPdcaAnswer('Parzialmente attuato', 'partially_implemented', 3);
                                                        await addPdcaAnswer('Verificato', 'verified', 4);
                                                        await addPdcaAnswer('Parzialmente verificato', 'partially_verified', 5);
                                                        await addPdcaAnswer('Migliorato', 'improved', 6);
                                                        await addPdcaAnswer('Parzialmente migliorato', 'partially_improved', 7);
                                                        await addPdcaAnswer('Non applicabile', 'na', 8);
                                                        
                                                        // Clear default answers when switching to pdca
                                                        updatedQuestion.defaultAnswers = [];
                                                        
                                                        // Important: update the state after all answers are created
                                                        setLocalQuestion(updatedQuestion);
                                                    })();
                                                } else if (newType === 'vocabulary') {
                                                    // Remove all answers for vocabulary type initially
                                                    // The answers will be loaded from the selected vocabulary
                                                    updatedQuestion.answers = [];
                                                    updatedQuestion.selectedVocabulary = null;
                                                } else if (newType === 'customVocabulary') {
                                                    // Remove all answers for custom vocabulary type initially
                                                    // The answers will be loaded from the selected custom vocabulary
                                                    updatedQuestion.answers = [];
                                                    updatedQuestion.selectedCustomVocabulary = null;
                                                    // Load custom vocabularies when this type is selected
                                                    loadCustomVocabularies();
                                                } else if (newType === 'departmentFunctionalArea') {
                                                    // Remove all answers for department/functional area type
                                                    // This type doesn't use predefined answers
                                                    updatedQuestion.answers = [];
                                                } else if (newType === 'open' || newType === 'openNumber' || newType === 'date' || newType === 'time' || newType === 'datetime') {
                                                    // Remove all answers for open type, openNumber type and date/time types
                                                    updatedQuestion.answers = [];
                                                } else if (newType === 'table') {
                                                    // Remove all answers for table type
                                                    updatedQuestion.answers = [];
                                                } else if (newType === 'period') {
                                                    // Period type does not use predefined answers
                                                    updatedQuestion.answers = [];
                                                } else if (localQuestion.questionType === 'boolean' || localQuestion.questionType === 'yesNoNA' || localQuestion.questionType === 'pdca') {
                                                    // When switching from boolean, yesNoNA or pdca to other question types,
                                                    // remove all predefined answers
                                                    updatedQuestion.answers = [];
                                                }
                                                
                                                                                        // Clear default answers when switching to question types that don't support them
                                        if (!['single', 'multiple', 'boolean', 'yesNoNA', 'pdca', 'customVocabulary'].includes(newType)) {
                                            updatedQuestion.defaultAnswers = [];
                                        }
                                        
                                        // Only update state immediately for question types that don't use async answer creation
                                        if (newType !== 'boolean' && newType !== 'yesNoNA' && newType !== 'pdca') {
                                            setLocalQuestion(updatedQuestion);
                                        }
                                            }}
                                            >
                                            <option value="single">{t('singleChoice', { ns: 'labels' })}</option>
                                            <option value="multiple">{t('multipleChoice', { ns: 'labels' })}</option>
                                            <option value="boolean">{t('trueFalse', { ns: 'labels' })}</option>
                                            <option value="yesNoNA">{t('yesNoNA', { ns: 'labels' })}</option>
                                            <option value="pdca">{t('pdca', { ns: 'labels' })}</option>
                                            <option value="open">{t('openAnswer', { ns: 'labels' })}</option>
                                            <option value="openNumber">{t('openNumberAnswer', { ns: 'labels' })}</option>
                                            <option value="vocabulary">{t('vocabulary', { ns: 'labels' }) || 'Vocabulary'}</option>
                                            <option value="customVocabulary">{t('customVocabulary', { ns: 'labels' }) || 'Vocabolario Personalizzato'}</option>
                                            <option value="departmentFunctionalArea">{t('departmentFunctionalArea', { ns: 'labels' })}</option>
                                            <option value="date">{t('date', { ns: 'labels' })}</option>
                                            <option value="time">{t('time', { ns: 'labels' })}</option>
                                            <option value="datetime">{t('datetime', { ns: 'labels' })}</option>
                                             <option value="period">{t('period', { ns: 'labels' })}</option>
                                            <option value="table">{t('table', { ns: 'labels' })}</option>
                                        </select>
                                    </div>
                                </div>
                                
                                {/* Vocabulary selector - only shown when question type is vocabulary */}
                                {localQuestion.questionType === 'vocabulary' && (
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">{t('selectVocabulary', { ns: 'labels' })}</label>
                                        <Choices
                                            ref={vocabularyRef}
                                            voc={vocabularyOptions}
                                            value="id"
                                            text="name"
                                            className="inputStyle blackText bg-white"
                                            selected={localQuestion.selectedVocabulary ? [{ id: localQuestion.selectedVocabulary }] : []}
                                            onChange={(e) => {
                                                const selectedVocabularyId = e.selected?.[0]?.id || "";
                                                if (!selectedVocabularyId) return;
                                                
                                                // Only update the selectedVocabulary without loading items
                                                // The vocabulary items will be loaded in compilerNew.js when needed
                                                setLocalQuestion(prev => ({
                                                    ...prev,
                                                    selectedVocabulary: selectedVocabularyId
                                                }));
                                            }}
                                            placeholder={t('selectVocabulary', { ns: 'labels' })}
                                            dropDownParent="root"
                                        />
                                    </div>
                                )}

                                {/* Custom Vocabulary selector - only shown when question type is customVocabulary */}
                                {localQuestion.questionType === 'customVocabulary' && (
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">{t('selectCustomVocabulary', { ns: 'labels' }) || 'Seleziona Vocabolario Personalizzato'}</label>
                                        {loadingCustomVocabularies && (
                                            <div className="text-muted small mb-2">
                                                <i className="fa fa-spinner fa-spin me-1"></i>
                                                {t('loadingVocabularies', { ns: 'labels' }) || 'Caricamento vocabolari...'}
                                            </div>
                                        )}

                                {/* Period options - only shown when question type is period */}
                                {localQuestion.questionType === 'period' && (
                                    <div className="mb-3">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="questionPeriodIncludeTime"
                                                checked={localQuestion.periodIncludeTime}
                                                onChange={(e) => setLocalQuestion(prev => ({ ...prev, periodIncludeTime: e.target.checked }))}
                                            />
                                            <label className="form-check-label" htmlFor="questionPeriodIncludeTime">
                                                {t('includeTime', { ns: 'labels' })}
                                            </label>
                                        </div>
                                    </div>
                                )}
                                        <Choices
                                            ref={customVocabularyRef}
                                            voc={customVocabularies}
                                            value="Id"
                                            text="Name"
                                            className="inputStyle blackText bg-white"
                                            selected={localQuestion.selectedCustomVocabulary ? [{ Id: localQuestion.selectedCustomVocabulary }] : []}
                                            onChange={(e) => {
                                                const selectedCustomVocabularyId = e.selected?.[0]?.Id || "";
                                                if (!selectedCustomVocabularyId) return;
                                                
                                                // Only update the selectedCustomVocabulary without loading items
                                                // The custom vocabulary items will be loaded in compilerNew.js when needed
                                                setLocalQuestion(prev => ({
                                                    ...prev,
                                                    selectedCustomVocabulary: selectedCustomVocabularyId
                                                }));
                                            }}
                                            placeholder={t('selectCustomVocabulary', { ns: 'labels' }) || 'Seleziona Vocabolario Personalizzato'}
                                            dropDownParent="root"
                                        />
                                    </div>
                                )}
                                
                                {/* Hide Partially checkbox - only shown when question type is pdca */}
                                {localQuestion.questionType === 'pdca' && (
                                    <div className="mb-3">
                                        <div className="form-check">
                                            <input 
                                                className="form-check-input" 
                                                type="checkbox" 
                                                id="questionHidePartially"
                                                checked={localQuestion.hidePartially}
                                                onChange={(e) => setLocalQuestion(prev => ({ ...prev, hidePartially: e.target.checked }))}
                                            />
                                            <label className="form-check-label" htmlFor="questionHidePartially">
                                                Nascondi Parzialmente
                                            </label>
                                        </div>
                                    </div>
                                )}
                                
                                {/* Period options above description when type is period */}
                                {localQuestion.questionType === 'period' && (
                                    <div className="mb-3">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="questionPeriodIncludeTime_top"
                                                checked={localQuestion.periodIncludeTime}
                                                onChange={(e) => setLocalQuestion(prev => ({ ...prev, periodIncludeTime: e.target.checked }))}
                                            />
                                            <label className="form-check-label" htmlFor="questionPeriodIncludeTime_top">
                                                {t('includeTime', { ns: 'labels' })}
                                            </label>
                                        </div>
                                    </div>
                                )}

                                <div className="mb-3">
                                    <label className="form-label fw-bold">{t('questionDescription', { ns: 'labels' })}</label>
                                    <Language_Text_Editor
                                        language={language}
                                        mode="textarea"
                                        text={localQuestion.description}
                                        onChange={(updated) => setLocalQuestion(prev => ({ ...prev, description: updated }))}
                                        colors={colors}
                                    />
                                </div>
                                
                                <div className="mb-3">
                                    <div className="form-check">
                                        <input 
                                            className="form-check-input" 
                                            type="checkbox" 
                                            id="questionMandatory"
                                            checked={localQuestion.mandatory}
                                            onChange={(e) => setLocalQuestion(prev => ({ ...prev, mandatory: e.target.checked }))}
                                        />
                                        <label className="form-check-label" htmlFor="questionMandatory">
                                            {t('mandatory', { ns: 'labels' })}
                                        </label>
                                    </div>
                                </div>
                                
                                <div className="mb-3">
                                    <div className="form-check">
                                        <input 
                                            className="form-check-input" 
                                            type="checkbox" 
                                            id="questionInvisible"
                                            checked={localQuestion.invisible}
                                            onChange={(e) => setLocalQuestion(prev => ({ ...prev, invisible: e.target.checked }))}
                                        />
                                        <label className="form-check-label" htmlFor="questionInvisible">
                                            {t('invisible', { ns: 'labels' })}
                                        </label>
                                    </div>
                                </div>
                                
                                <div className="mb-3">
                                    <div className="form-check">
                                        <input 
                                            className="form-check-input" 
                                            type="checkbox" 
                                            id="questionAllowObservations"
                                            checked={localQuestion.allowObservations}
                                            onChange={(e) => setLocalQuestion(prev => ({ ...prev, allowObservations: e.target.checked }))}
                                        />
                                        <label className="form-check-label" htmlFor="questionAllowObservations">
                                            {t('allowObservations', { ns: 'labels' })}
                                        </label>
                                    </div>
                                </div>
                                
                                <div className="mb-3">
                                    <label className="form-label fw-bold">{t('attachments', { ns: 'labels' })}</label>
                                    <div>
                                        <div className="form-check mb-2">
                                            <input 
                                                className="form-check-input" 
                                                type="checkbox" 
                                                id="questionAllowFileUpload"
                                                checked={localQuestion.allowFileUpload}
                                                onChange={(e) => setLocalQuestion(prev => ({ ...prev, allowFileUpload: e.target.checked }))}
                                            />
                                            <label className="form-check-label" htmlFor="questionAllowFileUpload">
                                                {t('allowFileUpload', { ns: 'labels' })}
                                            </label>
                                        </div>
                                        <div className="form-check mb-2">
                                            <input 
                                                className="form-check-input" 
                                                type="checkbox" 
                                                id="questionAllowLinkUpload"
                                                checked={localQuestion.allowLinkUpload}
                                                onChange={(e) => setLocalQuestion(prev => ({ ...prev, allowLinkUpload: e.target.checked }))}
                                            />
                                            <label className="form-check-label" htmlFor="questionAllowLinkUpload">
                                                {t('allowLinkUpload', { ns: 'labels' })}
                                            </label>
                                        </div>
                                        <div className="form-check">
                                            <input 
                                                className="form-check-input" 
                                                type="checkbox" 
                                                id="questionAllowTextUpload"
                                                checked={localQuestion.allowTextUpload}
                                                onChange={(e) => setLocalQuestion(prev => ({ ...prev, allowTextUpload: e.target.checked }))}
                                            />
                                            <label className="form-check-label" htmlFor="questionAllowTextUpload">
                                                {t('allowTextUpload', { ns: 'labels' })}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="mb-3">
                                    <label className="form-label fw-bold">{t('questionText', { ns: 'labels' })}</label>
                                    <EditorJSTextEditor
                                        language={language}
                                        text={localQuestion.questionText}
                                        onChange={(updated) => setLocalQuestion(prev => ({ ...prev, questionText: updated }))}
                                        colors={colors}
                                        placeholder={t('questionText', { ns: 'labels' })}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Visibility Condition Section */}
                    <div className="col-12 px-0">
                        <div className="card mb-3 w-100" style={{ backgroundColor: editContextColors.body }}>
                            <div className="card-header" 
                                style={{ backgroundColor: colors.secondaryLight }}>
                                <h6 className="mb-0">{t('visibilityCondition', { ns: 'labels' })}</h6>
                            </div>
                            <div className="card-body">
                                <Condition_Editor
                                    condition={localQuestion.visibilityCondition || null}
                                    onChange={(updatedCondition) =>
                                        setLocalQuestion(prev => ({ ...prev, visibilityCondition: updatedCondition }))
                                    }
                                    loadAvailableQuestions={loadAvailableQuestions}
                                    colors={colors}
                                    parentEditContext="question"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Files Section */}
                    <div className="col-12 px-0">
                        {_drawFilesSection()}
                    </div>

                    {/* Table Configuration Section - only for table questions */}
                    {localQuestion.questionType === 'table' && (
                        <div className="col-12 px-0">
                            <div className="card mb-3 w-100" style={{ backgroundColor: editContextColors.body }}>
                                <div className="card-header" 
                                    style={{ backgroundColor: colors.secondaryLight }}>
                                    <h6 className="mb-0">{t('tableConfiguration', { ns: 'labels' })}</h6>
                                </div>
                                <div className="card-body">
                                    {/* Table Settings */}
                                    <div className="row mb-3">
                                        <div className="col-md-6">
                                            <label className="form-label fw-bold">{t('minRows', { ns: 'labels' })}</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                min="0"
                                                value={localQuestion.tableSettings?.minRows || 1}
                                                onChange={(e) => setLocalQuestion(prev => ({
                                                    ...prev,
                                                    tableSettings: {
                                                        ...prev.tableSettings,
                                                        minRows: parseInt(e.target.value) || 1
                                                    }
                                                }))}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-bold">{t('maxRows', { ns: 'labels' })}</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                min="1"
                                                value={localQuestion.tableSettings?.maxRows || 10}
                                                onChange={(e) => setLocalQuestion(prev => ({
                                                    ...prev,
                                                    tableSettings: {
                                                        ...prev.tableSettings,
                                                        maxRows: parseInt(e.target.value) || 10
                                                    }
                                                }))}
                                            />
                                        </div>
                                    </div>

                                    {/* Table Options */}
                                    <div className="row mb-3">
                                        <div className="col-md-4">
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="allowAddRows"
                                                    checked={localQuestion.tableSettings?.allowAddRows ?? true}
                                                    onChange={(e) => setLocalQuestion(prev => ({
                                                        ...prev,
                                                        tableSettings: {
                                                            ...prev.tableSettings,
                                                            allowAddRows: e.target.checked
                                                        }
                                                    }))}
                                                />
                                                <label className="form-check-label" htmlFor="allowAddRows">
                                                    {t('allowAddRows', { ns: 'labels' })}
                                                </label>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="allowDeleteRows"
                                                    checked={localQuestion.tableSettings?.allowDeleteRows ?? true}
                                                    onChange={(e) => setLocalQuestion(prev => ({
                                                        ...prev,
                                                        tableSettings: {
                                                            ...prev.tableSettings,
                                                            allowDeleteRows: e.target.checked
                                                        }
                                                    }))}
                                                />
                                                <label className="form-check-label" htmlFor="allowDeleteRows">
                                                    {t('allowDeleteRows', { ns: 'labels' })}
                                                </label>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="showRowNumbers"
                                                    checked={localQuestion.tableSettings?.showRowNumbers ?? true}
                                                    onChange={(e) => setLocalQuestion(prev => ({
                                                        ...prev,
                                                        tableSettings: {
                                                            ...prev.tableSettings,
                                                            showRowNumbers: e.target.checked
                                                        }
                                                    }))}
                                                />
                                                <label className="form-check-label" htmlFor="showRowNumbers">
                                                    {t('showRowNumbers', { ns: 'labels' })}
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Table Columns Builder */}
                                    <TableColumnsBuilder
                                        columns={localQuestion.tableColumns || []}
                                        language={language}
                                        onColumnsChange={(updatedColumns) => setLocalQuestion(prev => ({
                                            ...prev,
                                            tableColumns: updatedColumns
                                        }))}
                                        colors={colors}
                                        customVocabularies={customVocabularies}
                                        loadingCustomVocabularies={loadingCustomVocabularies}
                                        loadCustomVocabularies={loadCustomVocabularies}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Default Answers Section - only for questions with predefined answers */}
                    {['single', 'multiple', 'boolean', 'yesNoNA', 'pdca', 'customVocabulary'].includes(localQuestion.questionType) && localQuestion.answers && localQuestion.answers.length > 0 && (
                        <div className="col-12 px-0">
                            <div className="card mb-3 w-100" style={{ backgroundColor: editContextColors.body }}>
                                <div className="card-header d-flex justify-content-between align-items-center" 
                                    style={{ backgroundColor: colors.secondaryLight, cursor: 'pointer' }}
                                    onClick={() => setShowDefaultAnswers(!showDefaultAnswers)}>
                                    <h6 className="mb-0">{t('defaultAnswers', { ns: 'labels' }) || 'Default Answers'}</h6>
                                    <div className="d-flex align-items-center">
                                        <span className="small text-muted me-2">
                                            {showDefaultAnswers ? (t('hide', { ns: 'labels' }) || 'Hide') : (t('show', { ns: 'labels' }) || 'Show')}
                                        </span>
                                        <i className={`fa fa-chevron-${showDefaultAnswers ? 'up' : 'down'} text-muted`}></i>
                                    </div>
                                </div>
                                
                                {/* Collapsible content */}
                                {showDefaultAnswers && (
                                    <div className="card-body">
                                        <div className="mb-3">
                                            <label className="form-label">{t('selectDefaultAnswers', { ns: 'labels' }) || 'Select default answers (optional)'}</label>
                                            <div className="text-muted small mb-2">
                                                {localQuestion.questionType === 'single' && (t('singleDefaultHelp', { ns: 'labels' }) || 'Select one answer that will be preselected by default')}
                                                {localQuestion.questionType === 'multiple' && (t('multipleDefaultHelp', { ns: 'labels' }) || 'Select one or more answers that will be preselected by default')}
                                                {localQuestion.questionType === 'boolean' && (t('booleanDefaultHelp', { ns: 'labels' }) || 'Select the default true/false value')}
                                                {localQuestion.questionType === 'yesNoNA' && (t('yesNoNADefaultHelp', { ns: 'labels' }) || 'Select the default yes/no/not applicable value')}
                                                {localQuestion.questionType === 'pdca' && (t('pdcaDefaultHelp', { ns: 'labels' }) || 'Select one or more default PDCA options')}
                                                {localQuestion.questionType === 'customVocabulary' && (t('customVocabularyDefaultHelp', { ns: 'labels' }) || 'Select one or more default vocabulary items')}
                                            </div>
                                            <div className="mt-3">
                                                {localQuestion.answers.filter(a => !a.invisible).map((answer, aIndex) => {
                                                    const answerId = answer.answerId || answer.id;
                                                    const answerText = getLocalizedText(answer.answerText, `Option ${aIndex + 1}`);
                                                    
                                                    // Check if this answer is in the default answers array
                                                    const isDefaultAnswer = localQuestion.defaultAnswers.includes(answerId);
                                                    
                                                    if (localQuestion.questionType === 'single' || localQuestion.questionType === 'boolean' || localQuestion.questionType === 'yesNoNA') {
                                                        // Radio buttons for single selection
                                                        return (
                                                            <div key={`default-radio-${answerId}`} className="form-check">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="radio"
                                                                    name="defaultAnswer"
                                                                    id={`default-radio-${answerId}`}
                                                                    value={answerId}
                                                                    checked={isDefaultAnswer}
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) {
                                                                            setLocalQuestion(prev => ({ 
                                                                                ...prev, 
                                                                                defaultAnswers: [answerId] 
                                                                            }));
                                                                        }
                                                                    }}
                                                                />
                                                                <label className="form-check-label" htmlFor={`default-radio-${answerId}`}>
                                                                    {answerText}
                                                                </label>
                                                            </div>
                                                        );
                                                    } else if (localQuestion.questionType === 'multiple' || localQuestion.questionType === 'pdca' || localQuestion.questionType === 'customVocabulary') {
                                                        // Checkboxes for multiple selection
                                                        return (
                                                            <div key={`default-checkbox-${answerId}`} className="form-check">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    id={`default-checkbox-${answerId}`}
                                                                    value={answerId}
                                                                    checked={isDefaultAnswer}
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) {
                                                                            // Add to default answers if not already present
                                                                            if (!localQuestion.defaultAnswers.includes(answerId)) {
                                                                                setLocalQuestion(prev => ({ 
                                                                                    ...prev, 
                                                                                    defaultAnswers: [...prev.defaultAnswers, answerId] 
                                                                                }));
                                                                            }
                                                                        } else {
                                                                            // Remove from default answers
                                                                            setLocalQuestion(prev => ({ 
                                                                                ...prev, 
                                                                                defaultAnswers: prev.defaultAnswers.filter(id => id !== answerId) 
                                                                            }));
                                                                        }
                                                                    }}
                                                                />
                                                                <label className="form-check-label" htmlFor={`default-checkbox-${answerId}`}>
                                                                    {answerText}
                                                                </label>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                })}
                                                
                                                {/* Clear default answers button */}
                                                {localQuestion.defaultAnswers.length > 0 && (
                                                    <div className="mt-2">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-secondary"
                                                            onClick={() => {
                                                                setLocalQuestion(prev => ({ 
                                                                    ...prev, 
                                                                    defaultAnswers: [] 
                                                                }));
                                                            }}
                                                        >
                                                            <i className="fa fa-times me-1"></i>
                                                            {t('clearDefaultAnswers', { ns: 'labels' }) || 'Clear Default Answers'}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Answers Section - only if not an open question */}
                    {localQuestion.questionType !== 'open' && 
                     localQuestion.questionType !== 'openNumber' && 
                     localQuestion.questionType !== 'date' && 
                     localQuestion.questionType !== 'time' && 
                     localQuestion.questionType !== 'datetime' && 
                     localQuestion.questionType !== 'vocabulary' && 
                     localQuestion.questionType !== 'customVocabulary' && 
                     localQuestion.questionType !== 'departmentFunctionalArea' && 
                     localQuestion.questionType !== 'table' &&
                     localQuestion.questionType !== 'period' && (
                        <div className="col-12 px-0">
                            <Answers_Builder
                                answers={localQuestion.answers || []}
                                language={language}
                                parentQuestionCode={localQuestion.questionCode}
                                onTextChange={(index, updatedTextArray) => {
                                    const answers = [...(localQuestion.answers || [])];
                                    answers[index] = {
                                        ...answers[index],
                                        answerText: updatedTextArray
                                    };
                                    setLocalQuestion(prev => ({ ...prev, answers }));
                                }}
                                onDelete={(index) => {
                                    // Don't allow deletion of vocabulary items if this is a vocabulary question
                                    if (localQuestion.questionType === 'vocabulary' || localQuestion.questionType === 'customVocabulary') {
                                        show_system_message({
                                            timeOut: 3000,
                                            message: `<strong class="text-warning">${t('cannotDeleteVocabularyItems', { ns: 'labels' })}</strong>`
                                        });
                                        return;
                                    }
                                    
                                    const answers = [...(localQuestion.answers || [])];
                                    const deletedAnswerId = answers[index].answerId || answers[index].id;
                                    
                                    // Remove from default answers if it was selected
                                    const updatedDefaultAnswers = localQuestion.defaultAnswers.filter(id => id !== deletedAnswerId);
                                    
                                    answers.splice(index, 1);
                                    
                                    // After deletion, regenerate all answer codes
                                    const updatedWithCodes = answers.map((answer, aIndex) => ({
                                        ...answer,
                                        answerCode: generateHierarchicalCodes.generateAnswerCode(
                                            localQuestion.questionCode, 
                                            aIndex
                                        )
                                    }));
                                    
                                    setLocalQuestion(prev => ({ 
                                        ...prev, 
                                        answers: updatedWithCodes,
                                        defaultAnswers: updatedDefaultAnswers 
                                    }));
                                }}
                                onAdd={async () => {
                                    // Don't allow adding custom answers for vocabulary questions
                                    if (localQuestion.questionType === 'vocabulary' || localQuestion.questionType === 'customVocabulary') {
                                        show_system_message({
                                            timeOut: 3000,
                                            message: `<strong class="text-warning">${t('cannotAddVocabularyItems', { ns: 'labels' })}</strong>`
                                        });
                                        return;
                                    }
                                    
                                    // Use the existing getEmptyEntity function
                                    const emptyAnswer = await getEmptyEntity('answer');
                                    if (!emptyAnswer) {
                                        console.error("Failed to get empty answer");
                                        return;
                                    }
                                    
                                    // Assign an answer code based on the question code and its position
                                    const answerIndex = (localQuestion.answers || []).length;
                                    const answerCode = generateHierarchicalCodes.generateAnswerCode(
                                        localQuestion.questionCode,
                                        answerIndex
                                    );
                                    
                                    const answerWithCode = {
                                        ...emptyAnswer,
                                        answerCode
                                    };
                                    
                                    const answers = [...(localQuestion.answers || []), answerWithCode];
                                    setLocalQuestion(prev => ({ ...prev, answers }));
                                }}
                                onReorder={(reordered) => {
                                    // Don't allow reordering for vocabulary questions
                                    if (localQuestion.questionType === 'vocabulary' || localQuestion.questionType === 'customVocabulary') {
                                        show_system_message({
                                            timeOut: 3000,
                                            message: `<strong class="text-warning">${t('cannotReorderVocabularyItems', { ns: 'labels' })}</strong>`
                                        });
                                        return;
                                    }
                                    
                                    // Update answer codes after reordering
                                    const updatedWithCodes = reordered.map((answer, aIndex) => ({
                                        ...answer,
                                        answerCode: generateHierarchicalCodes.generateAnswerCode(
                                            localQuestion.questionCode, 
                                            aIndex
                                        )
                                    }));
                                    
                                    setLocalQuestion(prev => ({ ...prev, answers: updatedWithCodes }));
                                }}
                                onEdit={(index) => {
                                    setEditingAnswerIndex(index);
                                }}
                                colors={colors}
                                parentEditContext="question"
                                isEditing={true}
                                isVocabularyType={localQuestion.questionType === 'vocabulary' || localQuestion.questionType === 'customVocabulary'}
                                isBooleanType={['boolean', 'trueFalse'].includes(localQuestion.questionType)}
                            />
                        </div>
                    )}

                    {/* Validation Rules Editor - Show only for multiple choice questions */}
                    {(localQuestion.questionType === 'multiple' || localQuestion.questionType === 'pdca') && (
                        <QuestionValidationEditor
                            validation={localQuestion.validation}
                            onChange={(updatedValidation) => {
                                setLocalQuestion(prev => ({ 
                                    ...prev, 
                                    validation: updatedValidation 
                                }));
                            }}
                            answers={localQuestion.answers || []}
                            questionType={localQuestion.questionType}
                            colors={colors}
                            isEditing={true}
                        />
                    )}


                </div>
            </div>
        </div>
    );
};

export { Question_Editor };
